import useApprovalPolicyHook from "@/hooks/use-approval-policy.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { ApprovalCategoryEditModal } from "@/pages/settings/approval-categories/widgets/ApprovalCategoryEditModal.tsx";
import { useApprovalCategories } from "@/queries/approval/use-approval-categories.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiApprovalCategoryType } from "@/types/approvals/approval-policy.types.ts";
import {
  Card,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  Stack,
  Tag,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function ApprovalCategoriesPage() {
  const { t } = useTranslation();
  const modalsService = useModals();
  const { data: categories, isLoading } = useApprovalCategories();
  const { getCategoryInfo } = useApprovalPolicyHook();

  useTitle(t("approval:settings_title"), t("common:settings"));

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.approvals.categoriesViewed);
  });

  return (
    <Page>
      <Page.Header
        title={t("approval:settings_title")}
        showBack={true}
        navigateTo={"/settings/policies"}
      />
      <Page.Content>
        <Stack
          gap={"md"}
          className={"px-16 pt-4 grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4"}
          direction={"horizontal"}
        >
          {isLoading ? (
            <Card isLoading={true} />
          ) : (
            <>
              {categories?.map((category) => {
                const locale = getCategoryInfo(category);

                return (
                  <Card
                    header={locale.title}
                    caption={locale.caption ?? ""}
                    key={category.id}
                    to={`/settings/approval-categories/${category.id}`}
                    buttonsSlot={
                      <Tag>
                        {t("plurals:policy", {
                          count: category.policies.length,
                        })}
                      </Tag>
                    }
                    menu={
                      category.type === ApiApprovalCategoryType.custom ? (
                        <DropdownMenuItem
                          title={t("common:edit")}
                          leftIcon={<FontAwesomeIcon icon={"fa-light fa-edit"} />}
                          onClick={() =>
                            modalsService.openModal(ApprovalCategoryEditModal, { category })
                          }
                        />
                      ) : undefined
                    }
                  />
                );
              })}
              <Card
                icon={"fa-plus"}
                header={t("approval:create_title")}
                onClick={() => modalsService.openModal(ApprovalCategoryEditModal, {})}
                variant={"action"}
              />
            </>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
}
